/**
 * Pure helpers for the bulk send flow — deriving what the screens show from a
 * template, and shaping the batch definition the create call wants.
 *
 * Note on `needsPhone`: door B gets it from the server, which returns
 * `signerSlots` alongside the hidden template. Door A has no such call yet, so
 * it is derived here from the template's own signer — see
 * `slotNeedsPhone`. Backend has been asked for a matching endpoint; until then
 * the two paths could in principle disagree.
 */

import { RequestFile, FileField, RequestParticipant } from "models/interfaces";
import { fromPhoneParts, toPhoneParts } from "helpers/methods";
import { fieldColor } from "screens/SignRequest/SignRequestHelper";
import strings from "global/constants/StringConstants";
import {
  BULK_SUBJECT_MAX,
  BulkBatchDefinition,
  BulkBatchSettings,
  BulkCreateResponse,
  BulkFixedSigner,
  BulkCellField,
  BulkCellIssue,
  BulkDeliveryChannel,
  BulkRoleAssignment,
  BulkRowCheck,
  BulkRowCheckSummary,
  BulkRowVerdict,
  BulkValidationIssue,
  BulkSecurityKind,
  BulkParsedRow,
  BulkSignerSlot,
  BulkSlotSecurityFlags,
  BULK_CHANNEL_LABELS,
  BULK_CHANNEL_OPTIONS,
  BULK_SECURITY_LABELS,
  bulkAccessCodeError,
  isBulkEmailValid,
  needsPhoneFor,
  phoneReasonFor,
  roleNeedsPhone,
} from "./BulkSendTypesAndValidation";
import {
  idvMethodsFor,
  idvSecurityKeyOf,
  isIdvSecurityKey,
} from "helpers/identityVerificationHelpers";

/** Field types the recipient fills in themselves — never CSV columns. */
const RECIPIENT_OWNED_FIELDS = [
  strings.signature,
  "initials",
  "date_signed",
  "attachment",
];

/** "Employee" → "EM", "HR Manager" → "HM". */
export const roleInitials = (label: string): string =>
  (label || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

/** The colour this slot already wears in the editor, so the two screens match. */
export const roleColor = (signerIndex: number): string =>
  fieldColor[signerIndex] || fieldColor[0];

/** "Signs 1st", "Signs 2nd" — the pill beside a role. */
export const orderLabel = (order?: number): string => {
  if (!order || order < 1) return "Signs any time";
  const suffix =
    order % 10 === 1 && order % 100 !== 11
      ? "st"
      : order % 10 === 2 && order % 100 !== 12
        ? "nd"
        : order % 10 === 3 && order % 100 !== 13
          ? "rd"
          : "th";
  return `Signs ${order}${suffix}`;
};

/** The channels a template's own signer is set up for. */
const slotChannels = (
  participant: RequestParticipant,
): BulkDeliveryChannel[] => {
  const channels = (participant.deliveryChannels ||
    []) as BulkDeliveryChannel[];
  return channels.length > 0 ? channels : ["EMAIL"];
};

/**
 * Security flags in this flow's own terms.
 *
 * OTP outranks an access code deliberately, because the server's own
 * validation does: a slot carrying both never has its code read, so it is an
 * OTP slot and the recipient list must not ask for a code nobody will type.
 */
export const securityKindFrom = (
  flags?: BulkSlotSecurityFlags | null,
): BulkSecurityKind => {
  const idvKey = idvSecurityKeyOf(flags);
  if (idvKey) return idvKey;
  if (!flags?.needSecuredAccess) return "none";
  if (flags.needPhoneValidation) return "sms";
  if (flags.needOTPValidation) return "otp";
  return "code";
};

/** How that signer proves who they are, in this flow's own terms. */
const slotSecurity = (participant: RequestParticipant): BulkSecurityKind =>
  securityKindFrom(participant.requestSecurity);

/**
 * The slots endpoint answers with the security *flags*, not with the word this
 * flow uses, so every slot is normalised on the way in — otherwise every
 * `slot.security === "sms"` test silently reads false against an object.
 */
export const normaliseSlots = (slots: BulkSignerSlot[]): BulkSignerSlot[] =>
  (slots ?? []).map((slot) => ({
    ...slot,
    channels: slot.channels?.length ? slot.channels : ["EMAIL"],
    security:
      typeof slot.security === "string"
        ? slot.security
        : securityKindFrom(slot.security as unknown as BulkSlotSecurityFlags),
  }));

/**
 * A slot is texted or phone-verified, so whoever fills it needs a phone number
 * — either a CSV column or, when the slot is pinned, a value typed once.
 */
const slotNeedsPhone = (participant: RequestParticipant): boolean =>
  needsPhoneFor(slotChannels(participant)) ||
  slotSecurity(participant) === "sms";

/** Labels of the fields placed for one slot, minus the ones the signer owns. */
export const fieldLabelsForSlot = (
  files: RequestFile[] | undefined,
  signerIndex: number,
): string[] => {
  const labels: string[] = [];
  (files || []).forEach((file: RequestFile) => {
    (file.fields || []).forEach((field: FileField) => {
      if (field.signerIndex !== signerIndex) return;
      if (RECIPIENT_OWNED_FIELDS.includes(field.type)) return;
      const label = field.tabLabel || field.label || field.name;
      if (label && !labels.includes(label)) labels.push(label);
    });
  });
  return labels;
};

/** Every field placed for a slot, including the ones the signer fills in. */
export const allFieldLabelsForSlot = (
  files: RequestFile[] | undefined,
  signerIndex: number,
): string[] => {
  const labels: string[] = [];
  (files || []).forEach((file: RequestFile) => {
    (file.fields || []).forEach((field: FileField) => {
      if (field.signerIndex !== signerIndex) return;
      const label = field.tabLabel || field.label || field.name;
      if (label && !labels.includes(label)) labels.push(label);
    });
  });
  return labels;
};

/**
 * The signer slots of a template, as the roles screen needs them. A slot with
 * no role name falls back to `Signer 1`, `Signer 2` — the same fallback the
 * generated CSV uses for its column headers.
 */
export const toSignerSlots = (template: any): BulkSignerSlot[] => {
  const participants: RequestParticipant[] = template?.signers || [];
  return participants.map((participant, index) => ({
    signerIndex: participant.id,
    label: (participant.requestRole || participant.name || "").trim()
      ? (participant.requestRole || participant.name).trim()
      : `Signer ${index + 1}`,
    order: participant.order ?? index + 1,
    cc: Boolean(participant.cc),
    needsPhone: slotNeedsPhone(participant),
    channels: slotChannels(participant),
    security: slotSecurity(participant),
    /*
      Back to the display string the phone control speaks. The server stores a
      split object, so without this a pinned role's number silently starts
      blank every time the template is re-picked.
    */
    phone: fromPhoneParts(
      (participant.phone ?? participant.requestSecurity?.authPhone) as any,
    ),
    delayDays: participant.delayDays ?? 0,
    delayHours: participant.delayHours ?? 0,
    fieldLabels: allFieldLabelsForSlot(template?.files, participant.id),
  }));
};

/** "Signature, Full name, Date" — the line under a role's name. */
export const fieldNote = (slot: BulkSignerSlot): string => {
  const labels = slot.fieldLabels || [];
  if (labels.length === 0) return "No fields placed";
  return labels.slice(0, 3).join(", ") + (labels.length > 3 ? "…" : "");
};

/** Roles start out varying only when the template has a single slot. */
export const initialRoleAssignments = (
  slots: BulkSignerSlot[],
): BulkRoleAssignment[] =>
  slots.map((slot, index) => ({
    slot,
    mode: index === 0 ? "vary" : "fixed",
    name: "",
    email: "",
    phone: slot.phone ?? "",
    channels: slot.channels ?? ["EMAIL"],
    security: slot.security ?? "none",
    accessCode: slot.accessCode ?? "",
    delayDays: slot.delayDays ?? 0,
    delayHours: slot.delayHours ?? 0,
  }));

/**
 * Delivery and security edited here belong to *this batch*, never to the
 * template it came from — so they are written onto a copy of the request that
 * `from-document` turns into the batch's own hidden template.
 */
export const applyRolesToSignRequest = (
  signRequest: any,
  assignments: BulkRoleAssignment[],
) => ({
  ...signRequest,
  signers: (signRequest?.signers ?? []).map((signer: RequestParticipant) => {
    const assignment = assignments.find(
      (role) => role.slot.signerIndex === signer.id,
    );
    if (!assignment) return signer;

    const needsPhone =
      needsPhoneFor(assignment.channels) || assignment.security === "sms";

    /* A delay only means anything after the first level. */
    const holdsBack = (assignment.slot.order ?? 1) > 1;

    return {
      ...signer,
      requestRole: assignment.slot.label,
      deliveryChannels: assignment.channels,
      delayDays: holdsBack ? assignment.delayDays : undefined,
      delayHours: holdsBack ? assignment.delayHours : undefined,
      requestSecurity: {
        ...(signer.requestSecurity ?? {}),
        // Identity verification is a security option of its own, never
        // combined with a code or OTP.
        needSecuredAccess:
          assignment.security !== "none" && !isIdvSecurityKey(assignment.security),
        identityVerificationMethods: idvMethodsFor(assignment.security),
        needOTPValidation: assignment.security === "otp",
        needPhoneValidation: assignment.security === "sms",
        /*
          A pinned role is one person, so their code lives on the template.
          A role that varies gets its code per row instead — the template must
          not carry one, or every recipient would share it.
        */
        securityCode:
          assignment.security === "code" && assignment.mode === "fixed"
            ? assignment.accessCode.trim() || null
            : null,
        /*
          `authPhone` is a `{ countryCode, number }` object on the server, and
          Jackson has no creator for a bare string — sending one fails the whole
          request with an unexplained 400 before any handler runs.

          Always written, never merely spread over: the source template may
          carry an `authPhone` of its own, and letting that survive would
          phone-verify this batch's signer against the wrong number.
        */
        authPhone: needsPhone ? (toPhoneParts(assignment.phone) ?? null) : null,
      },
    };
  }),
});

/** Has the sender changed delivery or security away from the template's own? */
export const rolesDifferFromTemplate = (
  assignments: BulkRoleAssignment[],
): boolean =>
  assignments.some(
    (assignment) =>
      JSON.stringify(assignment.channels) !==
        JSON.stringify(assignment.slot.channels ?? ["EMAIL"]) ||
      assignment.security !== (assignment.slot.security ?? "none") ||
      (assignment.security === "code" &&
        assignment.accessCode.trim() !== (assignment.slot.accessCode ?? "")) ||
      assignment.delayDays !== (assignment.slot.delayDays ?? 0) ||
      assignment.delayHours !== (assignment.slot.delayHours ?? 0),
  );

/**
 * Has this phone cell been left alone? The shared contact control seeds itself
 * with the country's dial code, so an untouched field reads `"+1"` rather than
 * empty — and a row of those is still a blank row, not a flagged one.
 */
export const isDialCodeOnly = (phone?: string): boolean => {
  const value = (phone || "").trim();
  return !value || /^\+?\d{0,3}[\s-]*$/.test(value);
};

export const toFixedSigners = (
  assignments: BulkRoleAssignment[],
): BulkFixedSigner[] =>
  assignments
    .filter((assignment) => assignment.mode === "fixed")
    .map((assignment) => ({
      signerIndex: assignment.slot.signerIndex,
      name: assignment.name.trim(),
      email: assignment.email.trim(),
      ...(roleNeedsPhone(assignment) && toPhoneParts(assignment.phone)
        ? { phone: toPhoneParts(assignment.phone) }
        : {}),
      ...(assignment.security === "code"
        ? { accessCode: assignment.accessCode.trim() }
        : {}),
    }));

/**
 * Slots the recipient list fills — the columns of the generated CSV.
 *
 * Delivery and security are taken from the assignment, not from the template
 * slot underneath it: the sender may have just changed them on the Signers
 * step, and it is that choice which decides whether this column asks for a
 * phone or an access code.
 */
export const varyingSlots = (
  assignments: BulkRoleAssignment[],
): BulkSignerSlot[] =>
  assignments
    .filter((assignment) => assignment.mode === "vary")
    .map((assignment) => ({
      ...assignment.slot,
      channels: assignment.channels,
      security: assignment.security,
    }));

export const fixedSignerIndexes = (
  assignments: BulkRoleAssignment[],
): number[] =>
  assignments
    .filter((assignment) => assignment.mode === "fixed")
    .map((assignment) => assignment.slot.signerIndex);

interface BuildDefinitionArgs {
  templateId: string;
  workspaceId?: string;
  settings: BulkBatchSettings;
  assignments: BulkRoleAssignment[];
}

export const buildBatchDefinition = ({
  templateId,
  workspaceId,
  settings,
  assignments,
}: BuildDefinitionArgs): BulkBatchDefinition => ({
  name: settings.batchName.trim(),
  templateId,
  ...(workspaceId ? { workspaceId } : {}),
  emailSubject: settings.emailSubject.trim(),
  emailMessage: settings.emailMessage,
  expirationDays: settings.reminders.expirationDays,
  needReminders: settings.reminders.needReminders,
  reminders: settings.reminders.needReminders
    ? {
        daysBeforeFirstReminder: settings.reminders.daysBeforeFirstReminder,
        daysBetweenReminders: settings.reminders.daysBetweenReminders,
      }
    : null,
  ...(settings.scheduleTime ? { scheduleTime: settings.scheduleTime } : {}),
  signRequestConfig: settings.permissions,
  fixedSigners: toFixedSigners(assignments),
});

/**
 * The subject a batch starts with, worded and capped exactly as the
 * create-request flow words its own: `"Sign with Inkfree: <files>"`, trimmed to
 * the 100 characters the field accepts.
 */
export const generatedSubject = (fileNames: string[]): string => {
  const names = fileNames.filter(Boolean);
  if (names.length === 0) return "";
  const subject = `${strings.SIGN_WITH_INKFREE}: ${names.join(", ")}`;
  return subject.length > BULK_SUBJECT_MAX
    ? `${subject.slice(0, BULK_SUBJECT_MAX - 3).trim()}...`
    : subject;
};

/**
 * What a merged subject or message looks like for one row — the preview only,
 * never what is sent. The server does the real substitution, so anything it
 * does not recognise is left visible rather than blanked.
 */
export const mergePreview = (
  text: string,
  values: Record<string, string>,
): string =>
  text.replace(/\{\{([^}]+)\}\}/g, (whole, token: string) => {
    const key = token.trim().toLowerCase();
    return values[key] ?? whole;
  });

/**
 * One cell of the recipient grid. Declared structurally rather than imported
 * from the grid, which already reads from this file.
 */
type BulkRowCell = { name: string; email: string; phone: string; code: string };

/**
 * The server's parsed rows, in the shape the recipient grid edits.
 *
 * Every varying slot gets a cell whether the file had a column for it or not,
 * so a role the CSV never mentioned — an access code column the sender did not
 * know to add — is an empty box to fill in rather than a reason to start over.
 */
export const typedRowsFromParsed = (
  parsed: BulkParsedRow[],
  slots: BulkSignerSlot[],
): Record<number, BulkRowCell>[] =>
  (parsed ?? []).map((row) =>
    slots.reduce((cells: Record<number, BulkRowCell>, slot) => {
      const recipient = row.recipients?.find(
        (entry) => entry.signerIndex === slot.signerIndex,
      );
      cells[slot.signerIndex] = {
        name: recipient?.name ?? "",
        email: recipient?.email ?? "",
        phone: fromPhoneParts(recipient?.phone),
        code: recipient?.accessCode ?? "",
      };
      return cells;
    }, {}),
  );

/**
 * Check the typed rows — the single derivation every part of step 4 reads.
 *
 * A row is blank until the sender types something into it, and blank rows are
 * ignored rather than flagged. A row is invalid when a slot it must fill is
 * missing an email, has one that isn't an address, or is missing a phone the
 * template will text. Everything else is ready.
 */
export const checkTypedRows = (
  rows: Record<
    number,
    { name: string; email: string; phone: string; code: string }
  >[],
  slots: BulkSignerSlot[],
  skipped: number[] = [],
): BulkRowCheckSummary => {
  const checks: BulkRowCheck[] = rows.map((row, index) => {
    const cells = slots.map((slot) => ({
      slot,
      cell: row[slot.signerIndex] ?? {
        name: "",
        email: "",
        phone: "",
        code: "",
      },
    }));

    const isBlank = cells.every(
      ({ cell }) =>
        !cell.name.trim() &&
        !cell.email.trim() &&
        isDialCodeOnly(cell.phone) &&
        !(cell.code || "").trim(),
    );
    const name =
      cells.find(({ cell }) => cell.name.trim())?.cell.name.trim() ||
      cells.find(({ cell }) => cell.email.trim())?.cell.email.trim() ||
      "";

    if (isBlank) {
      return { index, verdict: "blank" as const, name, reason: "", issues: [] };
    }
    if (skipped.includes(index)) {
      return {
        index,
        verdict: "skipped" as const,
        name,
        reason: "Skipped",
        issues: [],
      };
    }

    /*
      Every offending cell is collected, not just the first, so a row missing
      both a name and a valid email marks both inputs rather than sending the
      sender back a second time for the other one.
    */
    const issues: BulkCellIssue[] = [];
    cells.forEach(({ slot, cell }) => {
      const textsThisRole =
        needsPhoneFor(slot.channels ?? ["EMAIL"]) || slot.security === "sms";

      if (!cell.name.trim()) {
        issues.push({
          signerIndex: slot.signerIndex,
          field: "name",
          message: "Name is required",
          kind: "missing",
        });
      }
      if (!cell.email.trim()) {
        issues.push({
          signerIndex: slot.signerIndex,
          field: "email",
          message: "Email is required",
          kind: "missing",
        });
      } else if (!isBulkEmailValid(cell.email)) {
        issues.push({
          signerIndex: slot.signerIndex,
          field: "email",
          message: "Not a valid email address",
          kind: "malformed",
        });
      }
      /*
        An access code is per recipient, never per batch — so it is asked for
        here, in the row, the same way the phone is.
      */
      if (slot.security === "code") {
        const codeError = bulkAccessCodeError(cell.code);
        if (codeError) {
          issues.push({
            signerIndex: slot.signerIndex,
            field: "code",
            message: codeError,
            kind:
              codeError === "Access code is required" ? "missing" : "malformed",
          });
        }
      }
      /*
        Exactly the test the payload applies, so a number this accepts is a
        number the request can carry — rather than one that passes here and is
        dropped silently on the way out.
      */
      if (textsThisRole && !toPhoneParts(cell.phone)) {
        issues.push({
          signerIndex: slot.signerIndex,
          field: "phone",
          message: isDialCodeOnly(cell.phone)
            ? `Phone is required for ${phoneReasonFor(
                slot.channels ?? ["EMAIL"],
                slot.security ?? "none",
              )}`
            : "That number looks incomplete — check it",
          kind: isDialCodeOnly(cell.phone) ? "missing" : "malformed",
        });
      }
    });

    if (issues.length === 0) {
      return { index, verdict: "ready" as const, name, reason: "", issues };
    }

    /*
      A row holding something malformed reads as invalid; one that is merely
      unfinished reads as incomplete. The distinction is what the design's
      third chip counts, and it tells the sender which rows need thought
      rather than just typing.
    */
    const isMalformed = issues.some((issue) => issue.kind === "malformed");
    const missing = issues.filter((issue) => issue.kind === "missing");

    return {
      index,
      verdict: isMalformed ? ("invalid" as const) : ("incomplete" as const),
      name,
      reason:
        !isMalformed && missing.length > 1
          ? `${missing
              .map((issue) => issue.field)
              .join(" and ")} are both required`
          : issues.length === 1
            ? issues[0].message
            : `${issues[0].message}, and ${issues.length - 1} more`,
      issues,
      signerIndex: issues[0].signerIndex,
      field: issues[0].field,
    };
  });

  const count = (verdict: BulkRowVerdict) =>
    checks.filter((check) => check.verdict === verdict).length;

  return {
    checks,
    ready: count("ready"),
    invalid: count("invalid"),
    incomplete: count("incomplete"),
    blank: count("blank"),
    skipped: count("skipped"),
    checked: checks.length - count("blank"),
    flagged: checks.filter(
      (check) => check.verdict === "invalid" || check.verdict === "incomplete",
    ),
  };
};

/**
 * The server's own per-row report, shaped like the client's check so the same
 * panel renders both. Rows it flags carry no slot or field — the server saw a
 * file or a saved list, not the inputs on screen — so the panel offers no
 * "Fix above" for them.
 */
/**
 * Which cell a server issue is about.
 *
 * It says so outright when the server sends `signerIndex` and `field`. Failing
 * that the CSV header is read — `Student::Email` — by matching the role's own
 * label, which is how the header was built in the first place.
 */
const cellForIssue = (
  issue: BulkValidationIssue,
  slots: BulkSignerSlot[],
): { signerIndex: number; field: BulkCellField } | undefined => {
  if (typeof issue.signerIndex === "number" && issue.field) {
    return { signerIndex: issue.signerIndex, field: issue.field };
  }
  const [rawLabel, rawField] = (issue.column ?? "").split("::");
  const attribute = (rawField ?? rawLabel ?? "").trim().toLowerCase();
  const field: BulkCellField | undefined =
    attribute === "name"
      ? "name"
      : attribute === "email"
        ? "email"
        : attribute === "phone"
          ? "phone"
          : attribute.replace(/\s/g, "") === "accesscode"
            ? "code"
            : undefined;
  if (!field) return undefined;

  const label = (rawField ? rawLabel : "").trim().toLowerCase();
  const slot = label
    ? slots.find((entry) => entry.label.trim().toLowerCase() === label)
    : slots[0];
  return slot ? { signerIndex: slot.signerIndex, field } : undefined;
};

/**
 * The server's verdict laid over the browser's own, so a row the server
 * rejected is shown the way a row the browser rejected is shown: the cell
 * marked red where it sits, the message under it, the row in the worklist and
 * the counts agreeing with all three.
 *
 * Layering rather than replacing matters. A summary built from the report
 * alone holds only the flagged rows, so it cannot be indexed by row — the
 * grid would mark whichever row happened to sit at that position.
 */
export const mergeServerIssues = (
  summary: BulkRowCheckSummary,
  report: BulkCreateResponse | null,
  slots: BulkSignerSlot[],
): BulkRowCheckSummary => {
  const errors = (report?.issues ?? []).filter(
    (issue) => issue.severity === "error",
  );
  if (errors.length === 0) return summary;

  const checks = summary.checks.map((check) => ({
    ...check,
    issues: [...check.issues],
  }));

  errors.forEach((issue) => {
    const check = checks[issue.rowNumber - 1];
    if (!check || check.verdict === "skipped") return;
    const cell = cellForIssue(issue, slots);
    if (cell) {
      check.issues.push({
        signerIndex: cell.signerIndex,
        field: cell.field,
        message: issue.message,
        kind: "malformed",
      });
      check.signerIndex = check.signerIndex ?? cell.signerIndex;
      check.field = check.field ?? cell.field;
    }
    check.verdict = "invalid";
    /* The server's own words, which name the value it choked on. */
    check.reason = issue.message;
  });

  const count = (verdict: BulkRowVerdict) =>
    checks.filter((check) => check.verdict === verdict).length;

  return {
    checks,
    ready: count("ready"),
    invalid: count("invalid"),
    incomplete: count("incomplete"),
    blank: count("blank"),
    skipped: count("skipped"),
    checked: checks.length - count("blank"),
    flagged: checks.filter(
      (check) => check.verdict === "invalid" || check.verdict === "incomplete",
    ),
  };
};

/**
 * A report with no rows on screen behind it — the older upload and saved-list
 * paths, where the panel is all there is.
 *
 * Its `checks` hold only the flagged rows, so they are *not* aligned to row
 * indices: feed this to the recipient grid and it marks whichever row happens
 * to sit at that position. Use `mergeServerIssues` wherever rows are shown.
 */
export const summaryFromReport = (
  report: BulkCreateResponse,
): BulkRowCheckSummary => {
  const issues = report.issues ?? [];
  const errors = issues.filter((issue) => issue.severity === "error");
  const flagged: BulkRowCheck[] = errors.map((issue) => ({
    index: issue.rowNumber - 1,
    verdict: "invalid" as const,
    name: issue.column,
    reason: issue.message,
    issues: [],
  }));

  return {
    checks: flagged,
    ready: report.validRows,
    invalid: errors.length,
    incomplete: 0,
    blank: Math.max(report.totalRows - report.validRows - errors.length, 0),
    skipped: 0,
    checked: report.totalRows,
    flagged,
  };
};

/**
 * What was sent to `from-document` last time, reduced to the parts that decide
 * the template: the documents, where each field sits and who it belongs to,
 * and the roles themselves. Two passes through the editor that produce the
 * same signature need no second call — otherwise every trip back to fix a
 * field would leave another hidden template behind.
 */
export const layoutSignature = (signRequest: any): string => {
  const files = (signRequest?.files ?? []).map((file: RequestFile) => ({
    doc: file.unsignedDoc || file.unsignedPdf || "",
    fields: (file.fields ?? [])
      .map((field: FileField) =>
        [
          field.signerIndex,
          field.type,
          field.page,
          Math.round(field.x),
          Math.round(field.y),
          Math.round(field.w),
          Math.round(field.h),
          field.required ? 1 : 0,
        ].join(":"),
      )
      .sort(),
  }));
  const roles = (signRequest?.signers ?? []).map(
    (signer: RequestParticipant) =>
      `${signer.id}:${signer.requestRole ?? ""}:${signer.order ?? 0}`,
  );
  return JSON.stringify({ files, roles });
};

/**
 * The security every copy carries, read off the template's own signers — a
 * batch cannot set it, so this is a summary rather than a choice.
 */
export const securitySummary = (template: any): string => {
  const signers: RequestParticipant[] = template?.signers ?? [];
  const guarded = signers.filter(
    (signer) =>
      signer.requestSecurity?.needSecuredAccess ||
      idvSecurityKeyOf(signer.requestSecurity),
  );
  if (guarded.length === 0) return "No security";

  const describe = (signer: RequestParticipant) => {
    const security = signer.requestSecurity;
    const idvKey = idvSecurityKeyOf(security);
    if (idvKey) return BULK_SECURITY_LABELS[idvKey];
    if (security?.needPhoneValidation) return "SMS code";
    if (security?.needOTPValidation) return "Email OTP";
    return "Access code";
  };

  const kinds = Array.from(new Set(guarded.map(describe)));
  const roles = guarded
    .map((signer) => signer.requestRole || signer.name)
    .filter(Boolean);

  return roles.length > 0 && roles.length < signers.length
    ? `${kinds.join(" and ")} for ${roles.join(", ")}`
    : kinds.join(" and ");
};

/** How the link reaches recipients, again from the template's own slots. */
/**
 * How the batch reaches its recipients, read from the roles the sender
 * actually set rather than from the template they started with — that
 * snapshot is never refreshed, so it still says "Email" after a role is
 * switched to Text.
 */
export const deliverySummaryFor = (
  assignments: BulkRoleAssignment[],
): string => {
  const channels = new Set<BulkDeliveryChannel>();
  assignments
    .filter((assignment) => !assignment.slot.cc)
    .forEach((assignment) =>
      assignment.channels.forEach((channel) => channels.add(channel)),
    );
  if (channels.size === 0) return BULK_CHANNEL_LABELS.EMAIL;
  return BULK_CHANNEL_OPTIONS.filter((channel) => channels.has(channel))
    .map((channel) => BULK_CHANNEL_LABELS[channel])
    .join(" + ");
};

export const deliverySummary = (template: any): string => {
  const signers: RequestParticipant[] = template?.signers ?? [];
  const channels = new Set<string>();
  signers.forEach((signer) => {
    (signer.deliveryChannels ?? ["EMAIL"]).forEach((channel) =>
      channels.add(
        channel === "EMAIL" ? "Email" : channel === "SMS" ? "SMS" : "WhatsApp",
      ),
    );
  });
  if (channels.size === 0) return "Email";
  return Array.from(channels).join(" and ");
};

/** "After 5 days, then every 3 · expires in 30". */
export const remindersSummary = (template: any): string => {
  const expiry = template?.expirationDays
    ? `expires in ${template.expirationDays}`
    : "";
  if (!template?.needReminders || !template?.reminders) {
    return expiry ? `No reminders · ${expiry}` : "No reminders";
  }
  const { daysBeforeFirstReminder, daysBetweenReminders } = template.reminders;
  const cadence = `After ${daysBeforeFirstReminder} ${
    daysBeforeFirstReminder === 1 ? "day" : "days"
  }, then every ${daysBetweenReminders}`;
  return expiry ? `${cadence} · ${expiry}` : cadence;
};

/** "In 5 days" / "Off" — the right panel's one-line version. */
export const firstReminderSummary = (template: any): string => {
  if (!template?.needReminders || !template?.reminders) return "Off";
  const days = template.reminders.daysBeforeFirstReminder;
  return `In ${days} ${days === 1 ? "day" : "days"}`;
};

/** Hand a generated file to the browser. Used for the CSV template and export. */
export const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/** "125 recipients" / "1 recipient". */
export const recipientCountLabel = (count: number): string =>
  `${count} ${count === 1 ? "recipient" : "recipients"}`;

export const requestCountLabel = (count: number): string =>
  `${count} ${count === 1 ? "request" : "requests"}`;
